// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/services/polpo/PolpoArt.tsx
// 🔧 PURPOSE: Le figure di "Polpo Goloso" per la pagina e la card in Home
//   (sul tavolo le disegna PolpoCanvas, con gli stessi colori):
//   il polpo di Mola, i gabbiani (ognuno con la sua sciarpa), il tarallo,
//   il peperoncino e il panzerotto.
// ============================================

import React from 'react';

export const POLPO_COLORS = { body: '#E0457B', dark: '#9D174D', spot: '#F6A5C4' };
export const GULL_COLORS = { body: '#FFFFFF', wing: '#AEB9C6', beak: '#F2B33D', beakTip: '#D9480F', eye: '#1F2937', scared: '#8FB8EA' };
/** Le sciarpe dei quattro gabbiani: rosso, rosa, blu, arancio. */
export const SCARVES = ['#E53935', '#EC4899', '#2563EB', '#F59E0B'];
export const TARALLO_COLORS = { light: '#F0C46A', dark: '#8A5A12' };

type ArtProps = { className?: string; style?: React.CSSProperties };

export const PolpoSvg: React.FC<ArtProps> = ({ className, style }) => (
  <svg viewBox="0 0 48 48" className={className} style={style} aria-hidden="true">
    <g fill="none" stroke={POLPO_COLORS.body} strokeWidth="5" strokeLinecap="round">
      <path d="M12 30c-2 6-6 8-8 12" />
      <path d="M19 32c0 6-3 9-4 12" />
      <path d="M29 32c0 6 3 9 4 12" />
      <path d="M36 30c2 6 6 8 8 12" />
    </g>
    <ellipse cx="24" cy="21" rx="15.5" ry="14.5" fill={POLPO_COLORS.body} stroke={POLPO_COLORS.dark} strokeWidth="1.6" />
    <circle cx="14" cy="13" r="2" fill={POLPO_COLORS.spot} />
    <circle cx="33" cy="11" r="1.6" fill={POLPO_COLORS.spot} />
    <circle cx="35" cy="17" r="1.2" fill={POLPO_COLORS.spot} />
    <circle cx="18.5" cy="22" r="4.6" fill="#FFFFFF" />
    <circle cx="29.5" cy="22" r="4.6" fill="#FFFFFF" />
    <circle cx="20" cy="22.5" r="2.3" fill="#1F2937" />
    <circle cx="31" cy="22.5" r="2.3" fill="#1F2937" />
    <ellipse cx="24" cy="29.5" rx="2.6" ry="2" fill={POLPO_COLORS.dark} />
  </svg>
);

export const GullSvg: React.FC<ArtProps & { scarf?: string; scared?: boolean }> = ({ className, style, scarf = SCARVES[0], scared }) => (
  <svg viewBox="0 0 48 48" className={className} style={style} aria-hidden="true">
    <ellipse cx="22" cy="31" rx="14" ry="11.5" fill={scared ? GULL_COLORS.scared : GULL_COLORS.body} stroke="#6B7280" strokeWidth="1.2" />
    <path d="M11 29c4-6 12-7 17-3-4 5-11 7-17 3z" fill={GULL_COLORS.wing} />
    <circle cx="26" cy="16" r="10" fill={scared ? GULL_COLORS.scared : GULL_COLORS.body} stroke="#6B7280" strokeWidth="1.2" />
    <path d="M34 15l11 3-11 3z" fill={GULL_COLORS.beak} stroke="#B7791F" strokeWidth=".8" strokeLinejoin="round" />
    <circle cx="41" cy="18.6" r="1.3" fill={GULL_COLORS.beakTip} />
    <circle cx="29" cy="13" r="2.2" fill={GULL_COLORS.eye} />
    <circle cx="29.7" cy="12.3" r=".7" fill="#FFFFFF" />
    <path d="M16 23.5c6 3 13 3 19 0l.8 3.4c-6.4 3-13.6 3-20.4 0z" fill={scared ? '#94A3B8' : scarf} />
    <path d="M18 26l-4 7 5-1z" fill={scared ? '#94A3B8' : scarf} />
  </svg>
);

export const TaralloSvg: React.FC<ArtProps> = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
    <circle cx="12" cy="12" r="7" fill="none" stroke={TARALLO_COLORS.dark} strokeWidth="6" />
    <circle cx="12" cy="12" r="7" fill="none" stroke={TARALLO_COLORS.light} strokeWidth="4" />
    <path d="M7 9.5l2 1M14.5 6.5l1.2 1.8M17 14l-1.8.8M9.5 17l1-1.8" stroke={TARALLO_COLORS.dark} strokeWidth="1" strokeLinecap="round" />
  </svg>
);

export const PepperSvg: React.FC<ArtProps> = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
    <path d="M15.5 6.5c2.5 1.5 3 5 1 8.5-2.3 4-6.8 6.4-11 6.2 3.5-1.8 6-4.5 7-8 .6-2.4.8-4.8 3-6.7z" fill="#E11D48" stroke="#9F1239" strokeWidth="1" strokeLinejoin="round" />
    <path d="M15.6 6.6c.4-1.6 1.6-3 3.4-3.6M13.6 7.4c1-.6 2.6-.9 4 0" stroke="#16A34A" strokeWidth="1.8" strokeLinecap="round" fill="none" />
    <path d="M14 11c-.4 2-1.4 3.8-2.8 5.2" stroke="#FDA4AF" strokeWidth="1" strokeLinecap="round" fill="none" />
  </svg>
);

export const PanzerottoSvg: React.FC<ArtProps> = ({ className, style }) => (
  <svg viewBox="0 0 24 24" className={className} style={style} aria-hidden="true">
    <path d="M3 15a9 9 0 0118 0z" fill="#E9A93A" stroke="#9A5B12" strokeWidth="1.2" strokeLinejoin="round" />
    <path d="M3.5 15h17" stroke="#9A5B12" strokeWidth="1.2" />
    <g fill="#9A5B12">
      <circle cx="6" cy="13.6" r=".7" /><circle cx="9" cy="13.6" r=".7" /><circle cx="12" cy="13.6" r=".7" /><circle cx="15" cy="13.6" r=".7" /><circle cx="18" cy="13.6" r=".7" />
    </g>
    <path d="M8 9.5c1.5-1.2 3.3-1.6 5-1.2" stroke="#FCD9A0" strokeWidth="1.2" strokeLinecap="round" fill="none" />
  </svg>
);
